/**
 * WebhookDashboard - Webhook Monitoring Dashboard
 * Migrated from src/app/components/admin/WebhookDashboard.jsx
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import SkeletonLoaderComponents from '../components/shared/SkeletonLoader';
import DataTable from '../components/common/DataTable';

const { TableSkeleton } = SkeletonLoaderComponents;
import StatusBadge from '../components/common/StatusBadge';
import { API_ENDPOINTS } from '../config/api-endpoints';

const WebhookDashboard = () => {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchWebhooks = useCallback(async () => {
    try {
      setLoading(true);
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(`${API_ENDPOINTS.ADMIN.WEBHOOK_MANAGER}/admin/webhooks`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Webhook fetch failed: ${response.status}`);
      }
      const data = await response.json();
      setWebhooks(data.webhooks || []);
    } catch (err) {
      console.error('[WebhookDashboard] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWebhooks();
  }, [fetchWebhooks]);

  const columns = [
    {
      key: 'event',
      label: 'Event',
      render: (wh) => wh.eventType
    },
    {
      key: 'status',
      label: 'Status',
      render: (wh) => {
        const variant = wh.status === 'success' ? 'success' :
                       wh.status === 'pending' ? 'warning' : 'danger';
        return <StatusBadge text={wh.status} variant={variant} />;
      }
    },
    {
      key: 'timestamp',
      label: 'Timestamp',
      render: (wh) => new Date(wh.timestamp).toLocaleString()
    }
  ];

  if (loading) {
    return <TableSkeleton rows={8} columns={3} />;
  }

  return (
    <div className="webhook-dashboard">
      <h2>Webhook Monitoring</h2>

      {error && (
        <div className="admin-alert admin-alert--warning">
          <span>⚠️</span> {error}
        </div>
      )}

      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Recent Webhooks ({webhooks.length})</h3>
        </div>
        <DataTable
          columns={columns}
          data={webhooks}
          loading={loading}
          emptyMessage="No webhooks found"
        />
      </div>
    </div>
  );
};

export default WebhookDashboard;
